import { makeStyles } from "tss-react/mui";
import { Grid, Typography, TextField, Button, Switch } from "@mui/material";
import { ToggleSurface } from "../../common/core/Surface";
import { useTranslation } from "react-i18next";
import { useStation } from "../../../providers/StationProvider";
import { useCallback, useEffect, useState } from "react";
import LocationPicker from "../../common/inputs/LocationPicker";
import { Location } from "@emberly/zenith-client";
import LogoEditor from "../../common/inputs/LogoEditor";
import ResponsivePage from "../../common/core/ResponsivePage";
import config from "../../../config";
import AddressPicker from "../../common/inputs/AddressPicker";

const useStyles = makeStyles()(
  (theme) => ({
    switch: {
      "& .MuiSwitch-track": {
        backgroundColor: theme.palette.neutral.dark
      }
    },
    description: {
      marginBottom: theme.spacing(2),
      width: "100%"
    },
  })
);

export default function StationSettingsPage() {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { station, updateStation, exportSettings, updateExportSettings, orgId } = useStation();

  const [stationInfoWorking, setStationInfoWorking] = useState(false);
  const [legalInfoWorking, setLegalInfoWorking] = useState(false);
  const [notificationWorking, setNotificationWorking] = useState(false);

  const [displayName, setDisplayName] = useState(station?.displayName || "");
  const [legalName, setLegalName] = useState(station?.legalName || "");
  const [location, setLocation] = useState(station?.location || new Location());
  const [includeArrivedDest, setIncludeArrivedDest] = useState(station?.includeArrivedDest || false);

  const [organizationNumber, setOrganizationNumber] = useState(exportSettings?.organizationNumber || "")
  const [organizationLocation, setOrganizationLocation] = useState(exportSettings?.organizationLocation ? new Location(exportSettings.organizationLocation) : new Location());

  const [supportPhone, setSupportPhone] = useState(station?.supportPhone || "");
  const [supportEmail, setSupportEmail] = useState(station?.supportEmail || "");

  const [newRequestEmail, setNewRequestEmail] = useState(station?.newRequestEmail || false);

  useEffect(() => {
    if (!!station) {
      setDisplayName(t => t || station.displayName || "");
      setLegalName(t => t || station.legalName || "");
      setLocation(t => station.location || t || new Location());
      setIncludeArrivedDest(station.includeArrivedDest || false);
      setSupportEmail(t => t || station.supportEmail || "");
      setSupportPhone(t => t || station.supportPhone || "");
    }
  }, [station]);

  useEffect(() => {
    if (!!exportSettings) {
      setOrganizationNumber(t => t || exportSettings.organizationNumber || "");
      setOrganizationLocation(t => !!t?.address ? t : (exportSettings.organizationLocation ? new Location(exportSettings.organizationLocation) : new Location()))
    }
  }, [exportSettings]);

  const onSave = useCallback(async () => {
    try {
      setStationInfoWorking(true);
      await updateStation({
        displayName,
        legalName,
        location: location.hasCoordinates ? { coordinates: location.coordinates, address: location.address } : undefined,
        includeArrivedDest,
        supportEmail,
        supportPhone,
      });

    } catch (err) {
      console.log(err);
    } finally {
      setStationInfoWorking(false);
    }


  }, [updateStation, displayName, legalName, location, includeArrivedDest, supportPhone, supportEmail]);

  const stationInfoChanges = !!station && (
    (!!displayName && station?.displayName !== displayName) ||
    (!!legalName && station?.legalName !== legalName) ||
    (!!supportPhone && station?.supportPhone !== supportPhone) ||
    (!!supportEmail && station?.supportEmail !== supportEmail) ||
    (!!location && (!station.location || station.location.address !== location.address)) ||
    (station.includeArrivedDest !== includeArrivedDest)
  );

  const legalInfoChanges = !!exportSettings && (
    (!!organizationNumber && exportSettings?.organizationNumber !== organizationNumber) ||
    (!!organizationLocation && (!exportSettings.organizationLocation || exportSettings.organizationLocation.address !== organizationLocation.address))
  );


  const notificationChanges = !!station && (
    station.newRequestEmail !== newRequestEmail
  );

  const onSaveLegalSettings = useCallback(async () => {
    try {
      setLegalInfoWorking(true);
      await updateExportSettings({
        id: orgId,
        organizationNumber,
        organizationLocation: organizationLocation?.getData() || null,
      }, ["organizationNumber", "organizationLocation"]);

    } catch (err) {
      console.log(err);
    } finally {
      setLegalInfoWorking(false);
    }
  }, [updateExportSettings, organizationNumber, organizationLocation, orgId]);


  const onSaveNotifications = useCallback(async () => {
    try {
      setNotificationWorking(true);
      await updateStation({
        newRequestEmail
      });
    } catch (err) {
      console.log(err);
    } finally {
      setNotificationWorking(false);
    }

  }, [updateStation, newRequestEmail]);

  return (
    <ResponsivePage
      maxWidth={config.pageSize.small}
      title={t("stationSettings:header")}
    >
      <ToggleSurface
        title={
          <>
            <Typography variant="h5">{t("stationSettings:title")}</Typography>
            <Typography variant="body2" color="textSecondary" className={classes.description}>{t("stationSettings:description")}</Typography>
          </>
        }
      >
        <Grid item container spacing={2}>

          <Grid item xs={12}>
            <LogoEditor />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("common:name")}
              variant="filled"
              value={displayName}
              onChange={ev => setDisplayName(ev.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("stationSettings:legalName")}
              helperText={t("stationSettings:legalNameDescription")}
              variant="filled"
              value={legalName}
              onChange={ev => setLegalName(ev.target.value)}
              fullWidth
              size="small"
            />
          </Grid>


          <Grid item xs={12}>
            <LocationPicker
              picker
              label={t("entity:location:address")}
              value={location}
              onChange={setLocation}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("common:phoneNumber")}
              variant="filled"
              value={supportPhone}
              onChange={ev => setSupportPhone(ev.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("common:email")}
              variant="filled"
              value={supportEmail}
              onChange={ev => setSupportEmail(ev.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item container xs={12} spacing={2} alignItems="center">
            <Grid item xs container direction="column">
              <Typography variant="body1">
                {t("stationSettings:includeArrivedDest")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("stationSettings:includeArrivedDestDescription")}
              </Typography>
            </Grid>

            <Grid item>
              <Switch
                className={classes.switch}
                checked={includeArrivedDest}
                onChange={(ev, checked) => setIncludeArrivedDest(checked)}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" disabled={!stationInfoChanges || stationInfoWorking} onClick={onSave} loading={stationInfoWorking}>
              {t("common:saveChanges")}
            </Button>
          </Grid>
        </Grid>
      </ToggleSurface>


      <ToggleSurface
        title={
          <>
            <Typography variant="h5">{t("stationSettings:legalSettingsTitle")}</Typography>
            <Typography variant="body2" color="textSecondary" className={classes.description}>{t("stationSettings:legalSettingsDescription")}</Typography>
          </>
        }
      >

        <Grid item container spacing={2}>

          <Grid item xs={12}>
            <TextField
              label={t("stationSettings:organizationNumber")}
              variant="filled"
              value={organizationNumber}
              onChange={ev => setOrganizationNumber(ev.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <AddressPicker
              disabled={false}
              picker
              freeSolo
              label={t("stationSettings:organizationLocation")}
              value={organizationLocation}
              onChange={setOrganizationLocation}
            />

          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" disabled={!legalInfoChanges || legalInfoWorking} onClick={onSaveLegalSettings} loading={legalInfoWorking}>
              {t("common:saveChanges")}
            </Button>
          </Grid>
        </Grid>
      </ToggleSurface>


      <ToggleSurface
        title={
          <>
            <Typography variant="h5">{t("stationSettings:notificationSettingsTitle")}</Typography>
          </>
        }
      >

        <Grid item container spacing={2}>

          <Grid item container xs={12} spacing={2} alignItems="center">
            <Grid item xs container direction="column">
              <Typography variant="body1">
                {t("stationSettings:newRequest")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("stationSettings:newRequestDescription")}
              </Typography>
            </Grid>

            <Grid item>
              <Switch
                className={classes.switch}
                checked={newRequestEmail}
                onChange={(ev, checked) => setNewRequestEmail(checked)}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" disabled={!notificationChanges || notificationWorking} onClick={onSaveNotifications} loading={notificationWorking}>
              {t("common:saveChanges")}
            </Button>
          </Grid>
        </Grid>
      </ToggleSurface>

    </ResponsivePage>
  );
}


/*
 "notificationSettingsTitle": "Notifikasjoner",
    "newRequest": "Ny forespørsel",
    "newRequestDescription": "Send epost til stasjonsepost når en mottar nye forespørsler"
*/